import React from 'react';
import { UseFormRegister, FieldErrors } from 'react-hook-form';
import Input from '../../Form/Input';
import Select from '../../Form/Select';

export const CUSTOM_SOURCE_VALUE = '__custom';

interface CandidateFormData {
  appliedPosition: string;
  source: string;
  experience: number;
  expectedSalary: number;
  availableFrom: string;
  [key: string]: any;
}

interface CandidateApplicationDetailsProps {
  register: UseFormRegister<CandidateFormData>;
  errors: FieldErrors<CandidateFormData>;
  sourceOptions: { value: string; label: string }[];
  sourceValue: string;
  isCustomSource: boolean;
  onSourceSelect: (value: string) => void;
  cvFileName: string;
  onCvFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onCvFileClear: () => void;
  pdfFileName: string;
  onPdfFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPdfFileClear: () => void;
  isEditing: boolean;
}

const CandidateApplicationDetails: React.FC<CandidateApplicationDetailsProps> = ({
  register,
  errors,
  sourceOptions,
  sourceValue,
  isCustomSource,
  onSourceSelect,
  cvFileName,
  onCvFileChange,
  onCvFileClear,
  pdfFileName,
  onPdfFileChange,
  onPdfFileClear,
  isEditing,
}) => {
  return (
    <div className="border-b border-gray-200 pb-6">
      <h4 className="text-md font-medium text-gray-900 mb-4">Application Details</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Input
          label="Applied Position *"
          {...register('appliedPosition', { required: 'Applied position is required' })}
          error={errors.appliedPosition?.message}
          placeholder="e.g., Senior Captain"
        />

        <div>
          {/* A real dropdown for the known sources; "Other" reveals a free-text
              input so unlisted sources (Facebook, walk-in...) can be typed in. */}
          <Select
            label="Application Source *"
            value={isCustomSource ? CUSTOM_SOURCE_VALUE : sourceValue}
            onChange={(e) => onSourceSelect(e.target.value)}
            error={errors.source?.message}
            options={[...sourceOptions, { value: CUSTOM_SOURCE_VALUE, label: 'Other (type manually)' }]}
          />
          {isCustomSource && (
            <Input
              label="Type the source *"
              {...register('source', { required: 'Application source is required' })}
              error={errors.source?.message}
              placeholder="e.g., Facebook, Walk-in..."
              className="mt-3"
            />
          )}
        </div>

        <Input
          label="Years of Experience *"
          type="number"
          {...register('experience', { 
            required: 'Experience is required',
            min: { value: 0, message: 'Experience cannot be negative' }
          })}
          error={errors.experience?.message}
          placeholder="e.g., 5"
        />

        <Input
          label="Expected Salary (USD)"
          type="number"
          {...register('expectedSalary', { 
            min: { value: 0, message: 'Salary cannot be negative' }
          })}
          error={errors.expectedSalary?.message}
          placeholder="e.g., 5000"
        />

        <Input
          label="Available From"
          type="date"
          {...register('availableFrom')}
          error={errors.availableFrom?.message}
        />

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            CV / Resume File *
          </label>
          <div className="flex items-center space-x-3">
            <label className="flex-1 flex items-center justify-center px-4 py-3 border-2 border-dashed border-gray-300 rounded-md cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition">
              <svg className="w-5 h-5 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <span className="text-sm text-gray-600">
                {cvFileName || 'Click to upload CV file (Required)'}
              </span>
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={onCvFileChange}
                className="hidden"
                required={!isEditing}
              />
            </label>
            {cvFileName && (
              <button
                type="button"
                onClick={onCvFileClear}
                className="text-red-500 hover:text-red-700"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Accepted formats: PDF, DOC, DOCX (Required for new applications)
          </p>
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Application PDF
          </label>
          <div className="flex items-center space-x-3">
            <label className="flex-1 flex items-center justify-center px-4 py-3 border-2 border-dashed border-gray-300 rounded-md cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition">
              <svg className="w-5 h-5 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <span className="text-sm text-gray-600">
                {pdfFileName || 'Click to upload the signed application PDF (optional)'}
              </span>
              <input
                type="file"
                accept=".pdf"
                onChange={onPdfFileChange}
                className="hidden"
              />
            </label>
            {pdfFileName && (
              <button
                type="button"
                onClick={onPdfFileClear}
                className="text-red-500 hover:text-red-700"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
          <p className="mt-1 text-xs text-gray-500">
            PDF only — stored in the Application PDF column
          </p>
        </div>
      </div>
    </div>
  );
};

export default CandidateApplicationDetails;
